import { useMemo } from 'react'
import { useApp } from '../../context/AppContext'

/**
 * Validador de acceso basado en permisos de usuario.
 * * Este componente actúa como un wrapper que renderiza sus hijos (`children`) únicamente si el usuario
 * cuenta con los permisos necesarios definidos en el array `AccessNames`.
 * * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Elementos que se mostrarán si el acceso es concedido.
 * @param {string[]} [props.AccessNames=[]] - Lista de slugs (`vista_slug`) de los permisos requeridos.
 * @param {boolean} [props.showError=true] - Indica si se debe mostrar un mensaje de "Acceso denegado" en caso de fallo.
 * * @returns {JSX.Element|null} Retorna los hijos si hay acceso, un mensaje de error si no lo hay y showError es true, o null.
 */
const AuthAccessValidator = ({
  children,
  AccessNames = [],
  showError = true,
}) => {
  const { access = [] } = useApp()

  // ⚡ Convertimos a Set para búsquedas rápidas.
  // Dependemos del CONTENIDO (no de la referencia) para que un array literal
  // como AccessNames={['ticket_support']} —nuevo en cada render— no rompa la
  // memoización ni recalcule en cada render del padre.
  const accessKey = AccessNames.join('|')
  const requiredAccessSet = useMemo(
    () => new Set(AccessNames),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessKey]
  )

  // 🔍 Validación segura
  const hasAccess = useMemo(() => {
    if (!requiredAccessSet.size) return true // si no se requiere nada, permitir

    return access?.some(
      (perm) =>
        requiredAccessSet.has(perm?.vista_slug) && perm?.puede_ver === true
    )
  }, [access, requiredAccessSet])

  // ✅ Acceso permitido
  if (hasAccess) return <>{children}</>

  // ❌ No mostrar error
  if (!showError) return null

  return (
    <div
      className="alert alert-danger d-flex align-items-start gap-2"
      role="alert"
    >
      <i
        className="bi bi-shield-lock-fill flex-shrink-0"
        style={{ fontSize: '1.5rem' }}
      ></i>

      <div>
        <strong>Acceso denegado</strong>
        <div className="small mt-1">
          No tienes permisos para visualizar este contenido.
        </div>

        {AccessNames.length > 0 && (
          <div className="mt-2">
            <span className="fw-semibold">Permisos requeridos:</span>
            <div className="d-flex flex-wrap gap-1 mt-1">
              {AccessNames.map((role) => (
                <span key={role} className="badge bg-dark">
                  {role}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AuthAccessValidator
